/**
 * Model Manager modal for Ragsmith Web UI
 * Provides a comprehensive interface to browse, search, and select models
 */

import { fetchAllModels, fetchRateLimits, fetchModelUsage, loadModel } from './api.js';
import { escapeHtml } from './html-escape.js';
import { state, saveSessionStateWithModel } from './state.js';
import { addSystemMessage, elements } from './ui.js';
import { updateMenuLoadButton, updateHeaderStatus, getMenuElements } from './menu.js';

/** Modal state */
let modalOpen = false;
let allModels = [];
let filteredModels = [];
let currentFilter = {
    provider: 'all',
    search: '',
    showFreeOnly: false,
    showReasoningOnly: false,
    showToolingOnly: false,
    showVisionOnly: false,
    showRateLimitedOnly: false
};

// Track if tooling filter is required (agent has Tier 1 tools)
let toolingRequired = false;
let usageSummary = {};
let usageByProvider = {};  // Per-provider usage breakdown
let usageByModel = {};     // Per-model usage for current period
let usageDataCache = {};   // Cache for all period usage data { today: {...}, week: {...}, ... }
let currentUsagePeriod = 'today';  // "today", "week", "month", "all"
let currentUsageProvider = 'all';  // Provider filter for usage display
let rateLimits = {};
let selectedModelId = null;

/** Accordion state - which providers are expanded (all collapsed by default) */
let expandedProviders = {
    'local': false,
    'anthropic': false,
    'google': false,
    'openai': false,
    'groq': false,
    'openrouter': false
};

/** Sub-accordion state for OpenRouter provider sub-groups */
let expandedSubProviders = {};

/** Modal DOM elements */
let modalElements = {
    overlay: null,
    panel: null,
    closeBtn: null,
    searchInput: null,
    providerFilter: null,
    freeOnlyCheckbox: null,
    reasoningOnlyCheckbox: null,
    toolingOnlyCheckbox: null,
    visionOnlyCheckbox: null,
    rateLimitedOnlyCheckbox: null,
    modelList: null,
    selectBtn: null,
    usageSummaryEl: null,
    usagePeriodFilter: null,
    usageProviderFilter: null
};

/**
 * Bypass state management for OpenRouter free models.
 * Stores in localStorage: { "model:free": { enabled: true, resetAt: timestamp } }
 */
const BYPASS_KEY = 'openrouter_bypass';

function getBypassState() {
    try {
        return JSON.parse(localStorage.getItem(BYPASS_KEY) || '{}');
    } catch { return {}; }
}

export function isBypassed(modelId) {
    return getBypassState()[modelId]?.enabled === true;
}

export function setBypassed(modelId, enabled, resetAt = null) {
    const bypasses = getBypassState();
    if (enabled) {
        // Default reset: next midnight UTC if no specific timestamp
        if (!resetAt) {
            const tomorrow = new Date();
            tomorrow.setUTCHours(24, 0, 0, 0);
            resetAt = tomorrow.getTime();
        }
        bypasses[modelId] = { enabled: true, resetAt };
    } else {
        delete bypasses[modelId];
    }
    localStorage.setItem(BYPASS_KEY, JSON.stringify(bypasses));
}

function checkBypassResets() {
    const bypasses = getBypassState();
    const now = Date.now();
    let changed = false;
    for (const [modelId, info] of Object.entries(bypasses)) {
        if (info.enabled && info.resetAt && now > info.resetAt) {
            delete bypasses[modelId];
            changed = true;
        }
    }
    if (changed) {
        localStorage.setItem(BYPASS_KEY, JSON.stringify(bypasses));
    }
}

/**
 * Initialize Model Manager
 */
export function initModelManager() {
    // Create modal HTML
    createModalHTML();

    // Start bypass auto-reset timer (check every 60s)
    checkBypassResets();
    setInterval(checkBypassResets, 60000);

    // Get modal elements
    modalElements.overlay = document.getElementById('model-manager-overlay');
    modalElements.panel = document.getElementById('model-manager-panel');
    modalElements.closeBtn = document.getElementById('model-manager-close');
    modalElements.searchInput = document.getElementById('model-search');
    modalElements.providerFilter = document.getElementById('provider-filter');
    modalElements.freeOnlyCheckbox = document.getElementById('free-only');
    modalElements.reasoningOnlyCheckbox = document.getElementById('reasoning-only');
    modalElements.toolingOnlyCheckbox = document.getElementById('tooling-only');
    modalElements.visionOnlyCheckbox = document.getElementById('vision-only');
    modalElements.rateLimitedOnlyCheckbox = document.getElementById('rate-limited-only');
    modalElements.modelList = document.getElementById('model-list');
    modalElements.selectBtn = document.getElementById('model-select-btn');
    modalElements.usageSummaryEl = document.getElementById('usage-summary');
    modalElements.usagePeriodFilter = document.getElementById('usage-period-filter');
    modalElements.usageProviderFilter = document.getElementById('usage-provider-filter');

    // Event listeners
    modalElements.overlay?.addEventListener('click', closeModelManager);
    modalElements.closeBtn?.addEventListener('click', closeModelManager);
    modalElements.searchInput?.addEventListener('input', handleSearch);
    modalElements.providerFilter?.addEventListener('change', handleProviderChange);
    modalElements.freeOnlyCheckbox?.addEventListener('change', handleFilterChange);
    modalElements.reasoningOnlyCheckbox?.addEventListener('change', handleFilterChange);
    modalElements.toolingOnlyCheckbox?.addEventListener('change', handleFilterChange);
    modalElements.visionOnlyCheckbox?.addEventListener('change', handleFilterChange);
    modalElements.rateLimitedOnlyCheckbox?.addEventListener('change', handleFilterChange);
    modalElements.selectBtn?.addEventListener('click', handleSelectModel);
    modalElements.usagePeriodFilter?.addEventListener('change', handleUsageFilterChange);
    modalElements.usageProviderFilter?.addEventListener('change', handleUsageFilterChange);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            closeModelManager();
        }
    });

    // Note: model-selector-btn click is wired up in menu.js
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="model-manager-overlay" class="modal-overlay"></div>
        <div id="model-manager-panel" class="model-manager-panel">
            <div class="model-manager-header">
                <h2>Models</h2>
                <button id="model-manager-close" class="modal-close">&times;</button>
            </div>

            <div class="model-manager-toolbar">
                <div class="toolbar-row">
                    <input type="text" id="model-search" placeholder="Search models..." class="model-search">
                    <select id="provider-filter" class="toolbar-select">
                        <option value="all">All Providers</option>
                        <option value="local">Local</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="google">Google</option>
                        <option value="openai">OpenAI</option>
                        <option value="groq">Groq</option>
                        <option value="openrouter">OpenRouter</option>
                    </select>
                </div>
                <div class="toolbar-row toolbar-options">
                    <div class="toolbar-checkboxes">
                        <label class="checkbox-label">
                            <input type="checkbox" id="free-only"> Free
                        </label>
                        <label class="checkbox-label">
                            <input type="checkbox" id="reasoning-only"> Thinking
                        </label>
                        <label class="checkbox-label" id="tooling-only-label">
                            <input type="checkbox" id="tooling-only"> Tooling
                        </label>
                        <label class="checkbox-label" id="vision-only-label">
                            <input type="checkbox" id="vision-only"> Vision
                        </label>
                        <label class="checkbox-label">
                            <input type="checkbox" id="rate-limited-only"> Rate Limited
                        </label>
                    </div>
                    <div class="toolbar-usage">
                        <select id="usage-period-filter" class="toolbar-select-sm">
                            <option value="today">Today</option>
                            <option value="week">7 Days</option>
                            <option value="month">30 Days</option>
                            <option value="all">All Time</option>
                        </select>
                        <select id="usage-provider-filter" class="toolbar-select-sm">
                            <option value="all">All</option>
                            <option value="local">Local</option>
                            <option value="anthropic">Anthropic</option>
                            <option value="google">Google</option>
                            <option value="openai">OpenAI</option>
                            <option value="groq">Groq</option>
                            <option value="openrouter">OpenRouter</option>
                        </select>
                        <span id="usage-summary" class="usage-inline"></span>
                    </div>
                </div>
            </div>

            <div id="model-list" class="model-list">
                <div class="model-loading">Loading models...</div>
            </div>

            <div class="model-manager-footer">
                <button id="model-select-btn" class="menu-btn primary" disabled>Select Model</button>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Update the current model display in the menu
 */
export function updateCurrentModelDisplay(modelId, displayName, provider, isLoaded = false) {
    const display = document.getElementById('current-model-display');
    if (!display) return;

    display.textContent = displayName || 'No model';

    // Toggle green border on parent button when model is loaded
    const btn = document.getElementById('model-selector-btn');
    if (btn) {
        if (isLoaded) {
            btn.classList.add('has-selection');
        } else {
            btn.classList.remove('has-selection');
        }
    }
}

/**
 * Open the Model Manager modal
 */
export async function openModelManager() {
    modalOpen = true;
    selectedModelId = null;

    modalElements.overlay?.classList.add('active');
    modalElements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Reset filters and cache
    currentFilter = { provider: 'all', search: '', showFreeOnly: false, showReasoningOnly: false, showToolingOnly: toolingRequired, showVisionOnly: false, showRateLimitedOnly: false };
    currentUsagePeriod = 'today';
    currentUsageProvider = 'all';
    usageDataCache = {};
    if (modalElements.searchInput) modalElements.searchInput.value = '';
    if (modalElements.providerFilter) modalElements.providerFilter.value = 'all';
    if (modalElements.freeOnlyCheckbox) modalElements.freeOnlyCheckbox.checked = false;
    if (modalElements.reasoningOnlyCheckbox) modalElements.reasoningOnlyCheckbox.checked = false;
    if (modalElements.toolingOnlyCheckbox) {
        modalElements.toolingOnlyCheckbox.checked = toolingRequired;
        // Lock the checkbox if tooling is required
        modalElements.toolingOnlyCheckbox.disabled = toolingRequired;
    }
    // Update the label to show required state
    const toolingLabel = document.getElementById('tooling-only-label');
    if (toolingLabel) {
        toolingLabel.classList.toggle('required', toolingRequired);
        toolingLabel.title = toolingRequired ? 'Required: Current agent uses LLM tools' : '';
    }
    if (modalElements.usagePeriodFilter) modalElements.usagePeriodFilter.value = 'today';
    if (modalElements.usageProviderFilter) modalElements.usageProviderFilter.value = 'all';

    // Update select button state
    updateSelectButton();

    // Load models
    await loadModels();
}

/**
 * Close the Model Manager modal
 */
export function closeModelManager() {
    modalOpen = false;
    modalElements.overlay?.classList.remove('active');
    modalElements.panel?.classList.remove('open');
    document.body.style.overflow = '';

    // Reset accordion initialization so it re-evaluates loaded model on next open
    expandedProviders._initialized = false;
    expandedSubProviders._initialized = false;
}

/**
 * Load all models from API
 */
async function loadModels() {
    modalElements.modelList.innerHTML = '<div class="model-loading">Loading models...</div>';

    try {
        // Fetch models, rate limits, and all usage periods in parallel
        const [modelsData, rateLimitsData, todayUsage, weekUsage, monthUsage, allUsage] = await Promise.all([
            fetchAllModels(),
            fetchRateLimits().catch(() => ({ rate_limits: {} })),
            fetchModelUsage('today').catch(() => ({})),
            fetchModelUsage('week').catch(() => ({})),
            fetchModelUsage('month').catch(() => ({})),
            fetchModelUsage('all').catch(() => ({}))
        ]);

        allModels = modelsData.models || [];
        computeWorkers = modelsData.workers || null;  // admin-only; null hides the section
        rateLimits = rateLimitsData.rate_limits || {};

        // Cache all usage data for client-side filtering
        usageDataCache = {
            today: todayUsage,
            week: weekUsage,
            month: monthUsage,
            all: allUsage
        };

        // Set initial usage data from cache and render
        applyUsageFilter();
        renderUsageSummary();

        // Apply filters and render model list
        applyFilters();
    } catch (e) {
        console.error('[ModelManager] Load failed:', e);
        modalElements.modelList.innerHTML = '<div class="model-error">Failed to load models</div>';
    }
}

/**
 * Handle usage filter changes (period or provider)
 * Uses cached data instead of making API calls
 */
function handleUsageFilterChange() {
    currentUsagePeriod = modalElements.usagePeriodFilter?.value || 'today';
    currentUsageProvider = modalElements.usageProviderFilter?.value || 'all';

    applyUsageFilter();
    renderUsageSummary();
    renderModelList();  // Re-render model cards with updated usage stats
}

/**
 * Apply current usage filter from cached data
 */
function applyUsageFilter() {
    const periodData = usageDataCache[currentUsagePeriod] || {};

    if (currentUsageProvider === 'all') {
        // Use full period data
        usageSummary = periodData.summary || {};
        usageByProvider = periodData.by_provider || {};
        usageByModel = periodData.models || {};
    } else {
        // Filter by provider - need to recalculate summary from models
        const allModelsData = periodData.models || {};
        const filteredModels = {};
        let totalRequests = 0;
        let totalPromptTokens = 0;
        let totalCompletionTokens = 0;

        for (const [modelId, stats] of Object.entries(allModelsData)) {
            if (stats.provider === currentUsageProvider) {
                filteredModels[modelId] = stats;
                totalRequests += stats.request_count || 0;
                totalPromptTokens += stats.prompt_tokens || 0;
                totalCompletionTokens += stats.completion_tokens || 0;
            }
        }

        usageSummary = {
            total_requests: totalRequests,
            total_prompt_tokens: totalPromptTokens,
            total_completion_tokens: totalCompletionTokens
        };
        usageByProvider = {};  // Not relevant when filtering by single provider
        usageByModel = filteredModels;
    }
}

/**
 * Render usage summary
 */
function renderUsageSummary() {
    if (!modalElements.usageSummaryEl) return;

    const totalRequests = usageSummary.total_requests || 0;
    const totalPrompt = usageSummary.total_prompt_tokens || 0;
    const totalCompletion = usageSummary.total_completion_tokens || 0;
    const totalTokens = totalPrompt + totalCompletion;

    let html = '';

    // Compact inline display with provider breakdown when showing all providers
    if (totalRequests > 0) {
        // Show per-provider breakdown if "All Providers" is selected
        if (currentUsageProvider === 'all' && Object.keys(usageByProvider).length > 0) {
            const providerIcons = { local: '💻', anthropic: '🔶', google: '✨', openai: '🟢', groq: '⚡', openrouter: '🌐' };
            let providerHtml = '';

            for (const [provider, stats] of Object.entries(usageByProvider)) {
                if (stats.request_count > 0) {
                    providerHtml += `<span class="usage-provider" title="${provider}">${providerIcons[provider] || '📊'} ${stats.request_count}</span>`;
                }
            }

            if (providerHtml) {
                html = `${providerHtml} · <span class="usage-tokens">~${formatTokens(totalTokens)}</span>`;
            } else {
                html = `<span class="usage-calls">${totalRequests}</span> calls · <span class="usage-tokens">~${formatTokens(totalTokens)}</span>`;
            }
        } else {
            html = `<span class="usage-calls">${totalRequests}</span> calls · <span class="usage-tokens">~${formatTokens(totalTokens)}</span>`;
        }
    } else {
        html = `<span class="usage-empty">No usage</span>`;
    }

    // Rate limit info (OpenRouter) - always show if available
    if (rateLimits.openrouter) {
        const rl = rateLimits.openrouter;
        const remaining = rl.limit_remaining ?? 0;
        const total = rl.limit_total ?? 50;
        const resetTs = rl.reset_timestamp;

        // Format reset time
        let resetText = '';
        if (resetTs) {
            const resetDate = new Date(resetTs);
            const now = new Date();
            if (resetDate > now) {
                const hoursUntil = Math.ceil((resetDate - now) / (1000 * 60 * 60));
                resetText = hoursUntil > 24
                    ? ` (resets ${resetDate.toLocaleDateString()})`
                    : ` (resets in ${hoursUntil}h)`;
            }
        }

        const isExhausted = remaining === 0;
        const statusClass = isExhausted ? 'rate-limit-exhausted' : (remaining <= 10 ? 'rate-limit-low' : '');
        html += `
            <div class="usage-row">
                <span class="usage-stat rate-limit ${statusClass}" title="OpenRouter free tier daily limit${resetText}">
                    🌐 <strong>${remaining}/${total}</strong> free requests remaining
                </span>
            </div>
        `;
    }

    modalElements.usageSummaryEl.innerHTML = html;
    modalElements.usageSummaryEl.style.display = html ? 'block' : 'none';
}

/**
 * Format token count for display
 */
function formatTokens(count) {
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
}

/**
 * Apply filters and re-render model list
 */
function applyFilters() {
    filteredModels = allModels.filter(model => {
        // Provider filter
        if (currentFilter.provider !== 'all' && model.provider !== currentFilter.provider) {
            return false;
        }

        // Free only filter
        if (currentFilter.showFreeOnly && !model.is_free) {
            return false;
        }

        // Reasoning only filter
        if (currentFilter.showReasoningOnly && !model.is_reasoning) {
            return false;
        }

        // Tooling only filter
        if (currentFilter.showToolingOnly && !model.supports_tooling) {
            return false;
        }

        // Vision only filter
        if (currentFilter.showVisionOnly && !model.supports_vision) {
            return false;
        }

        // Rate limited only filter
        if (currentFilter.showRateLimitedOnly && !model.is_rate_limited) {
            return false;
        }

        // Search filter
        if (currentFilter.search) {
            const searchLower = currentFilter.search.toLowerCase();
            const matchName = model.display_name.toLowerCase().includes(searchLower);
            const matchProvider = model.provider_name.toLowerCase().includes(searchLower);
            const matchId = model.id.toLowerCase().includes(searchLower);
            if (!matchName && !matchProvider && !matchId) {
                return false;
            }
        }

        return true;
    });

    renderModelList();
}

/**
 * Render the filtered model list with accordion groups
 */
/**
 * Render a single model card
 * @param {boolean} showProvider - Show provider tag on card (for "Other" group)
 */
function renderModelCard(model, usageByModel, showProvider = false) {
    const isSelected = model.id === selectedModelId;
    const isAvailable = model.is_available;
    const isCurrentModel = state.loadedModel && (model.id === state.loadedModel || (model.tier && model.tier === state.loadedModel));

    return `
        <div class="model-card ${isSelected ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''} ${isCurrentModel ? 'current' : ''}"
             data-model-id="${escapeHtml(model.id)}"
             data-tier="${escapeHtml(model.tier || '')}">
            <div class="model-card-content">
                <div class="model-card-header">
                    <span class="model-name">${escapeHtml(model.display_name)}${showProvider && model.provider_name ? `<span class="model-provider-tag">${escapeHtml(model.provider_name)}</span>` : ''}</span>
                    <div class="model-badges">
                        ${model.is_free ? '<span class="badge free">FREE</span>' : ''}
                        ${model.is_rate_limited ? '<span class="badge rate-limited">RATE LIMITED</span>' : ''}
                        ${model.is_reasoning ? '<span class="badge reasoning">THINKING</span>' : ''}
                        ${model.supports_tooling ? (model.tooling_level === 'heavy' ? '<span class="badge tooling-heavy">HEAVY TOOLS</span>' : '<span class="badge tooling-light">LIGHT TOOLS</span>') : ''}
                        ${model.supports_vision ? '<span class="badge vision">VISION</span>' : ''}
                        ${!isAvailable ? '<span class="badge unavailable">N/A</span>' : ''}
                        ${isCurrentModel ? '<span class="badge current">LOADED</span>' : ''}
                    </div>
                </div>
                <div class="model-card-details">
                    ${model.context_length ? `<span class="model-context">${formatContext(model.context_length)} ctx</span>` : ''}
                    ${model.size !== 'Cloud' ? `<span class="model-size">${escapeHtml(model.size)}</span>` : ''}
                    ${formatPricing(model)}
                </div>
                ${model.description ? `<div class="model-description">${escapeHtml(model.description)}</div>` : ''}
                ${model.is_rate_limited && model.is_free && model.id.endsWith(':free') ? `
                <div class="model-bypass-toggle" data-model-id="${escapeHtml(model.id)}">
                    <label class="bypass-switch" onclick="event.stopPropagation()">
                        <input type="checkbox" class="bypass-checkbox" ${isBypassed(model.id) ? 'checked' : ''}>
                        <span class="bypass-slider"></span>
                    </label>
                    <span class="bypass-label">${isBypassed(model.id) ? 'Using balance' : 'Free (rate limited)'}</span>
                    ${model.rate_limit_info ? `<span class="bypass-limits">${model.rate_limit_info.requests_per_minute && model.rate_limit_info.requests_per_minute > 0 ? model.rate_limit_info.requests_per_minute + ' req/min' : ''}${model.rate_limit_info.requests_per_minute > 0 && model.rate_limit_info.requests_per_day > 0 ? ' · ' : ''}${model.rate_limit_info.requests_per_day && model.rate_limit_info.requests_per_day > 0 ? model.rate_limit_info.requests_per_day.toLocaleString() + ' req/day' : ''}</span>` : ''}
                </div>
                ` : ''}
                ${renderModelUsage(usageByModel[model.tier] || usageByModel[model.id])}
            </div>
        </div>
    `;
}

/**
 * Admin-only: per-worker placement panel for the Local group. Shows each
 * GPU worker's health, VRAM headroom, warm (resident) models, and installed
 * models — clicking a chip loads that model on that specific worker.
 */
function renderWorkersPanel() {
    if (!computeWorkers || computeWorkers.length === 0) return '';
    let html = '<div class="mm-workers">';
    for (const w of computeWorkers) {
        const healthy = w.enabled && w.llm_healthy;
        const gpu = w.gpu || {};
        const total = gpu.vram_total_mb || 0;
        const used = gpu.vram_used_mb || 0;
        const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
        const inflight = Object.values(w.in_flight || {}).reduce((a, b) => a + b, 0);
        const isEmbed = (m) => m.toLowerCase().includes('embed');
        const warm = (w.warm_models || []).filter(m => !isEmbed(m));
        const installed = (w.installed_models || []).filter(m => !isEmbed(m) && !warm.includes(m));
        const chip = (name, isWarm) => {
            const size = (w.model_sizes || {})[name] || 0;
            const fits = !total || !size || size <= total * 1048576;
            const sizeGb = size ? (size / 1073741824).toFixed(1) + ' GB' : '';
            const cls = `mm-worker-chip${isWarm ? ' warm' : ''}${!fits ? ' nofit' : ''}`;
            const title = !fits
                ? `${sizeGb} won't fit in ${(total / 1024).toFixed(1)} GB VRAM`
                : isWarm
                    ? 'Warm on this worker — switches instantly'
                    : `Load onto ${w.name}${sizeGb ? ' · ' + sizeGb : ''}`;
            const dis = (!healthy || !fits) ? ' data-disabled="1"' : '';
            return `<button class="${cls}" data-worker-model="${name}" data-worker-id="${w.id}" title="${title}"${dis}>${isWarm ? '⚡ ' : ''}${name}</button>`;
        };
        html += `
            <div class="mm-worker ${healthy ? '' : 'down'}">
                <div class="mm-worker-head">
                    <span class="mm-worker-dot ${healthy ? 'ok' : 'bad'}"></span>
                    <span class="mm-worker-name">${w.name}</span>
                    ${total ? `<span class="mm-worker-vram"><span class="mm-vram-bar"><span class="mm-vram-fill${pct > 85 ? ' hot' : ''}" style="width:${pct}%"></span></span><span class="mm-vram-text">${(used / 1024).toFixed(1)}/${(total / 1024).toFixed(1)} GB</span></span>` : ''}
                    ${inflight ? `<span class="mm-worker-busy">${inflight} in flight</span>` : ''}
                    ${!healthy ? '<span class="mm-worker-offline">offline</span>' : ''}
                </div>
                <div class="mm-worker-models">
                    ${warm.map(m => chip(m, true)).join('')}
                    ${installed.map(m => chip(m, false)).join('')}
                    ${!warm.length && !installed.length ? '<span class="mm-worker-none">no models installed</span>' : ''}
                </div>
            </div>`;
    }
    html += '</div>';
    return html;
}

async function handleWorkerChipClick(btn) {
    if (btn.dataset.disabled) return;
    const name = btn.dataset.workerModel;
    const workerId = parseInt(btn.dataset.workerId, 10);
    const model = allModels.find(m => m.provider === 'local' && (m.id === name || m.tier === name))
        || { id: name, display_name: name, provider: 'local' };
    state.selectedModel = model.tier || model.id;
    state.selectedModelData = { ...model, _workerId: workerId };
    syncModelSelectors(model, model.tier || model.id);
    closeModelManager();
    await loadSelectedModel();
}

function renderModelList() {
    if (!modalElements.modelList) return;

    if (filteredModels.length === 0) {
        modalElements.modelList.innerHTML = '<div class="model-empty">No models match your filters</div>';
        return;
    }

    // Group by provider
    const grouped = {};
    for (const model of filteredModels) {
        const providerKey = model.provider;
        if (!grouped[providerKey]) {
            grouped[providerKey] = [];
        }
        grouped[providerKey].push(model);
    }

    // Find which provider (and sub-provider) contains the currently loaded model
    let loadedModelProvider = null;
    let loadedModelSubProvider = null;
    if (state.loadedModel) {
        for (const model of filteredModels) {
            if (model.id === state.loadedModel || (model.tier && model.tier === state.loadedModel)) {
                loadedModelProvider = model.provider;
                if (model.provider === 'openrouter' && model.provider_name) {
                    loadedModelSubProvider = model.provider_name;
                }
                break;
            }
        }
    }

    // Auto-expand only the provider with the loaded model (on first render)
    // Don't override user's manual expand/collapse choices
    if (loadedModelProvider && !expandedProviders._initialized) {
        expandedProviders = {
            'local': false,
            'anthropic': false,
            'groq': false,
            'openrouter': false,
            '_initialized': true
        };
        expandedProviders[loadedModelProvider] = true;
    }

    // Render accordion groups
    let html = '';
    const providerOrder = ['local', 'anthropic', 'google', 'openai', 'groq', 'openrouter'];
    const providerLabels = {
        'local': 'Local (Ollama)',
        'anthropic': 'Anthropic',
        'google': 'Google',
        'openai': 'OpenAI',
        'groq': 'Groq',
        'openrouter': 'OpenRouter'
    };
    const providerIcons = {
        'local': '💻',
        'anthropic': '🔶',
        'google': '✨',
        'openai': '🟢',
        'groq': '⚡',
        'openrouter': '🌐'
    };

    for (const provider of providerOrder) {
        if (!grouped[provider]) continue;

        const isExpanded = expandedProviders[provider];
        const modelCount = grouped[provider].length;

        html += `
            <div class="model-accordion ${isExpanded ? 'expanded' : ''}" data-provider="${provider}">
                <div class="accordion-header" data-provider="${provider}">
                    <div class="accordion-title">
                        <span class="accordion-icon">${providerIcons[provider]}</span>
                        <span class="accordion-label">${providerLabels[provider]}</span>
                        <span class="accordion-count">${modelCount}</span>
                    </div>
                    <span class="accordion-chevron">▾</span>
                </div>
                <div class="accordion-content">
                    <div class="accordion-inner">
        `;

        // Admin-only: compute-worker placement panel at the top of Local —
        // shows what's warm where and lets the admin place a model on a
        // specific GPU worker.
        if (provider === 'local') {
            html += renderWorkersPanel();
        }

        // For OpenRouter, sub-group by provider_name (Google, OpenAI, etc.)
        if (provider === 'openrouter') {
            const subGroups = {};
            for (const model of grouped[provider]) {
                const sub = model.provider_name || 'Other';
                if (!subGroups[sub]) subGroups[sub] = [];
                subGroups[sub].push(model);
            }
            // Major providers (2+ models) get own accordion, rest go to "Other"
            const MIN_FOR_OWN_GROUP = 2;
            const majorSubs = [];
            const otherModels = [];
            for (const [name, models] of Object.entries(subGroups)) {
                if (models.length >= MIN_FOR_OWN_GROUP) {
                    majorSubs.push({ name, models });
                } else {
                    otherModels.push(...models);
                }
            }
            // Sort major groups by model count descending, then alphabetically
            majorSubs.sort((a, b) => b.models.length - a.models.length || a.name.localeCompare(b.name));
            // Add "Other" at the end if there are any
            if (otherModels.length > 0) {
                majorSubs.push({ name: 'Other', models: otherModels });
            }

            // Auto-expand the sub-group containing the loaded model (first render only)
            if (loadedModelSubProvider && !expandedSubProviders._initialized) {
                expandedSubProviders._initialized = true;
                // Find which group the loaded model ended up in
                for (const { name, models: subModels } of majorSubs) {
                    if (subModels.some(m => m.id === state.loadedModel || (m.tier && m.tier === state.loadedModel))) {
                        expandedSubProviders[`openrouter_${name}`] = true;
                        break;
                    }
                }
            }

            for (const { name: subName, models: subModels } of majorSubs) {
                const subKey = `openrouter_${subName}`;
                const subExpanded = expandedSubProviders[subKey] || false;
                const isOther = subName === 'Other';
                html += `
                    <div class="sub-accordion ${subExpanded ? 'expanded' : ''}" data-sub-provider="${escapeHtml(subKey)}">
                        <div class="sub-accordion-header" data-sub-provider="${escapeHtml(subKey)}">
                            <span class="sub-accordion-label">${escapeHtml(subName)}</span>
                            <span class="sub-accordion-count">${subModels.length}</span>
                            <span class="sub-accordion-chevron">▾</span>
                        </div>
                        <div class="sub-accordion-content">
                            <div class="sub-accordion-inner">
                `;
                for (const model of subModels) {
                    html += renderModelCard(model, usageByModel, isOther);
                }
                html += `
                            </div>
                        </div>
                    </div>
                `;
            }
        } else {
            for (const model of grouped[provider]) {
                html += renderModelCard(model, usageByModel);
            }
        }

        html += `
                    </div>
                </div>
            </div>
        `;
    }

    modalElements.modelList.innerHTML = html;

    // Initialize expanded accordions with proper height (so first click animates correctly)
    const expandedAccordions = modalElements.modelList.querySelectorAll('.model-accordion.expanded');
    expandedAccordions.forEach(accordion => {
        const content = accordion.querySelector('.accordion-content');
        const inner = accordion.querySelector('.accordion-inner');
        if (content && inner) {
            content.style.height = inner.offsetHeight + 'px';
            // Set to auto after a brief delay so it can grow if content changes
            setTimeout(() => {
                if (expandedProviders[accordion.dataset.provider]) {
                    content.style.height = 'auto';
                }
            }, 50);
        }
    });

    // Add click handlers to accordion headers
    const headers = modalElements.modelList.querySelectorAll('.accordion-header');
    headers.forEach(header => {
        header.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleAccordion(header.dataset.provider);
        });
    });

    // Initialize expanded sub-accordions with proper height (for auto-expand on first render)
    const expandedSubs = modalElements.modelList.querySelectorAll('.sub-accordion.expanded');
    expandedSubs.forEach(subAccordion => {
        const subContent = subAccordion.querySelector('.sub-accordion-content');
        const subInner = subAccordion.querySelector('.sub-accordion-inner');
        if (subContent && subInner) {
            subContent.style.height = subInner.offsetHeight + 'px';
            setTimeout(() => {
                const subKey = subAccordion.dataset.subProvider;
                if (expandedSubProviders[subKey]) {
                    subContent.style.height = 'auto';
                }
            }, 50);
        }
    });

    // Add click handlers to sub-accordion headers (OpenRouter sub-groups)
    const subHeaders = modalElements.modelList.querySelectorAll('.sub-accordion-header');
    subHeaders.forEach(header => {
        header.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleSubAccordion(header.dataset.subProvider);
        });
    });

    // Add click handlers to model cards
    const cards = modalElements.modelList.querySelectorAll('.model-card:not(.unavailable)');
    cards.forEach(card => {
        card.addEventListener('click', () => handleModelCardClick(card));
    });

    // Worker placement chips (admin-only panel inside the Local group)
    modalElements.modelList.querySelectorAll('.mm-worker-chip').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleWorkerChipClick(btn);
        });
    });

    // Add bypass toggle handlers
    const toggles = modalElements.modelList.querySelectorAll('.bypass-checkbox');
    toggles.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            const toggle = e.target.closest('.model-bypass-toggle');
            const modelId = toggle.dataset.modelId;
            const enabled = e.target.checked;
            const label = toggle.querySelector('.bypass-label');

            setBypassed(modelId, enabled);
            label.textContent = enabled ? 'Using balance' : 'Free (rate limited)';

            // No longer mutate state.loadedModel here — chat.js strips :free at send time
            // via isBypassed() check, keeping the original model ID intact for UI matching
        });
    });

    // Scroll to the currently loaded model instantly (no animation) after heights settle
    if (state.loadedModel) {
        setTimeout(() => {
            const currentCard = modalElements.modelList.querySelector('.model-card.current');
            if (currentCard) {
                const containerRect = modalElements.modelList.getBoundingClientRect();
                const cardRect = currentCard.getBoundingClientRect();
                const scrollOffset = cardRect.top - containerRect.top + modalElements.modelList.scrollTop - 10;
                modalElements.modelList.scrollTop = scrollOffset;
            }
        }, 60);
    }
}

/**
 * Toggle accordion open/closed with smooth animation
 */
function toggleAccordion(provider) {
    const accordion = modalElements.modelList.querySelector(`.model-accordion[data-provider="${provider}"]`);
    if (!accordion) return;

    const content = accordion.querySelector('.accordion-content');
    const inner = accordion.querySelector('.accordion-inner');
    if (!content || !inner) return;

    const isCurrentlyExpanded = expandedProviders[provider];

    if (isCurrentlyExpanded) {
        // Collapse: first set explicit height (in case it's 'auto'), then animate to 0
        const currentHeight = inner.offsetHeight;
        content.style.height = currentHeight + 'px';
        // Force reflow to ensure the height is applied before transitioning
        void content.offsetHeight;
        // Now animate to 0
        content.style.height = '0px';
        accordion.classList.remove('expanded');
        expandedProviders[provider] = false;
    } else {
        // Expand: set target height and animate
        const targetHeight = inner.offsetHeight;
        accordion.classList.add('expanded');
        content.style.height = targetHeight + 'px';
        expandedProviders[provider] = true;

        // After animation completes, set to auto and smooth scroll
        const onTransitionEnd = () => {
            if (expandedProviders[provider]) {
                content.style.height = 'auto';
                // Smooth scroll the accordion header toward the top of the modal
                const scrollContainer = modalElements.modelList;
                const headerRect = accordion.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const scrollOffset = headerRect.top - containerRect.top + scrollContainer.scrollTop - 10;
                scrollContainer.scrollTo({ top: scrollOffset, behavior: 'smooth' });
            }
            content.removeEventListener('transitionend', onTransitionEnd);
        };
        content.addEventListener('transitionend', onTransitionEnd);
    }
}

/**
 * Toggle sub-accordion (OpenRouter provider sub-groups)
 */
function toggleSubAccordion(subProvider) {
    const accordion = modalElements.modelList.querySelector(`.sub-accordion[data-sub-provider="${subProvider}"]`);
    if (!accordion) return;

    const content = accordion.querySelector('.sub-accordion-content');
    const inner = accordion.querySelector('.sub-accordion-inner');
    if (!content || !inner) return;

    const isCurrentlyExpanded = expandedSubProviders[subProvider];

    if (isCurrentlyExpanded) {
        const currentHeight = inner.offsetHeight;
        content.style.height = currentHeight + 'px';
        void content.offsetHeight;
        content.style.height = '0px';
        accordion.classList.remove('expanded');
        expandedSubProviders[subProvider] = false;
    } else {
        const targetHeight = inner.offsetHeight;
        accordion.classList.add('expanded');
        content.style.height = targetHeight + 'px';
        expandedSubProviders[subProvider] = true;

        const onTransitionEnd = () => {
            if (expandedSubProviders[subProvider]) {
                content.style.height = 'auto';
                // Smooth scroll sub-accordion into view
                const scrollContainer = modalElements.modelList;
                const headerRect = accordion.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const scrollOffset = headerRect.top - containerRect.top + scrollContainer.scrollTop - 10;
                scrollContainer.scrollTo({ top: scrollOffset, behavior: 'smooth' });
            }
            content.removeEventListener('transitionend', onTransitionEnd);
        };
        content.addEventListener('transitionend', onTransitionEnd);
    }

    // Recalculate parent accordion height since sub-accordion changed
    const parentAccordion = accordion.closest('.model-accordion');
    if (parentAccordion) {
        const parentContent = parentAccordion.querySelector('.accordion-content');
        if (parentContent) {
            parentContent.style.height = 'auto';
        }
    }
}

/**
 * Render model usage stats
 */
function renderModelUsage(usage) {
    if (!usage || usage.request_count === 0) {
        return '';  // Don't show anything for unused models
    }

    const totalTokens = (usage.prompt_tokens || 0) + (usage.completion_tokens || 0);
    const totalCalls = usage.request_count;
    const callsLabel = totalCalls === 1 ? '1 call' : `${totalCalls} calls`;

    return `
        <div class="model-usage">
            <span class="usage-chats">${callsLabel}</span>
            <span class="usage-tokens">~${formatTokens(totalTokens)} tokens</span>
        </div>
    `;
}

/**
 * Format pricing for a model (per 1M tokens)
 * Returns a pill showing "$in / $out per 1M" or "Free" for local/free models.
 */
function formatPricing(model) {
    if (model.provider === 'local') {
        return '<span class="model-price free">Free</span>';
    }
    if (model.is_free && !model.pricing) {
        return '<span class="model-price free">Free</span>';
    }
    const p = model.pricing;
    if (!p || (p.input == null && p.output == null)) return '';
    const fmt = (n) => {
        if (n == null) return '—';
        if (n === 0) return '$0';
        if (n < 0.01) return '$' + n.toFixed(4);
        if (n < 1) return '$' + n.toFixed(3);
        return '$' + n.toFixed(2);
    };
    // Hybrid free / overflow-priced (Google Flash on free-tier eligible
    // projects). Show "Free tier" prominently with the overflow price
    // in the tooltip so users know both the default cost and what
    // happens if they exceed the quota.
    if (model.is_free) {
        return `<span class="model-price free" title="Free on Google's free tier · Overflow: ${fmt(p.input)} / ${fmt(p.output)} per 1M tokens">Free tier</span>`;
    }
    return `<span class="model-price" title="Price per 1M tokens (input / output)">${fmt(p.input)} / ${fmt(p.output)} per 1M</span>`;
}

/**
 * Format context length
 */
function formatContext(length) {
    if (length >= 1000000) return (length / 1000000).toFixed(0) + 'M';
    if (length >= 1000) return (length / 1000).toFixed(0) + 'K';
    return length.toString();
}

/**
 * Handle model card click
 */
function handleModelCardClick(card) {
    // Deselect previous
    const prev = modalElements.modelList.querySelector('.model-card.selected');
    if (prev) prev.classList.remove('selected');

    // Select new
    card.classList.add('selected');
    selectedModelId = card.dataset.modelId;

    updateSelectButton();
}

/**
 * Update select button state
 */
function updateSelectButton() {
    if (!modalElements.selectBtn) return;

    if (selectedModelId) {
        const model = allModels.find(m => m.id === selectedModelId);
        modalElements.selectBtn.disabled = false;
        modalElements.selectBtn.textContent = `Select ${model?.display_name || 'Model'}`;
    } else {
        modalElements.selectBtn.disabled = true;
        modalElements.selectBtn.textContent = 'Select Model';
    }
}

/**
 * Handle search input
 */
function handleSearch(e) {
    currentFilter.search = e.target.value.trim();
    applyFilters();
}

/**
 * Handle provider filter change
 */
function handleProviderChange(e) {
    currentFilter.provider = e.target.value;
    applyFilters();
}

/**
 * Handle checkbox filter changes
 */
function handleFilterChange() {
    currentFilter.showFreeOnly = modalElements.freeOnlyCheckbox?.checked || false;
    currentFilter.showReasoningOnly = modalElements.reasoningOnlyCheckbox?.checked || false;
    // Only allow unchecking tooling if it's not required
    if (!toolingRequired) {
        currentFilter.showToolingOnly = modalElements.toolingOnlyCheckbox?.checked || false;
    }
    currentFilter.showVisionOnly = modalElements.visionOnlyCheckbox?.checked || false;
    currentFilter.showRateLimitedOnly = modalElements.rateLimitedOnlyCheckbox?.checked || false;
    applyFilters();
}

/**
 * Handle model selection - just updates selection state, doesn't load
 * Loading happens when user clicks the Load button in the menu
 * Exception: when in retry mode, selection triggers automatic load
 */
function syncModelSelectors(model, modelToSelect) {
    // Update menu dropdown to match
    const menuElements = getMenuElements();
    if (menuElements.modelSelect) {
        // Try to find matching option by tier or model ID
        let found = false;
        for (const opt of menuElements.modelSelect.options) {
            if (opt.value === model.tier || opt.value === model.id || opt.dataset?.modelName === model.id) {
                menuElements.modelSelect.value = opt.value;
                found = true;
                break;
            }
        }
        // If not found in dropdown (e.g. OpenRouter model), add it dynamically
        if (!found) {
            const opt = document.createElement('option');
            opt.value = modelToSelect;
            opt.textContent = model.display_name;
            // Use provider name as tier, display_name for model to avoid "Name (Name)" duplication
            const providerDisplay = model.provider ? model.provider.charAt(0).toUpperCase() + model.provider.slice(1) : 'OpenRouter';
            opt.dataset.tier = providerDisplay;
            opt.dataset.model = model.display_name;
            opt.dataset.modelName = model.id;
            opt.dataset.provider = model.provider || 'openrouter';
            opt.dataset.isReasoning = model.is_reasoning ? 'true' : 'false';
            menuElements.modelSelect.appendChild(opt);
            menuElements.modelSelect.value = modelToSelect;
        }

        // Also sync to hidden model select (used by chat.js for message headers)
        if (elements.modelSelect) {
            // Check if option exists in hidden select
            let hiddenFound = false;
            for (const opt of elements.modelSelect.options) {
                if (opt.value === modelToSelect) {
                    elements.modelSelect.value = modelToSelect;
                    hiddenFound = true;
                    break;
                }
            }
            // Add option if not found
            if (!hiddenFound) {
                const opt = document.createElement('option');
                opt.value = modelToSelect;
                opt.textContent = model.display_name;
                const providerDisplay = model.provider ? model.provider.charAt(0).toUpperCase() + model.provider.slice(1) : 'OpenRouter';
                opt.dataset.tier = providerDisplay;
                opt.dataset.model = model.display_name;
                opt.dataset.modelName = model.id;
                opt.dataset.provider = model.provider || 'openrouter';
                opt.dataset.isReasoning = model.is_reasoning ? 'true' : 'false';
                elements.modelSelect.appendChild(opt);
                elements.modelSelect.value = modelToSelect;
            }
        }
    }

    // Update the current model display in the menu (show as selected, not loaded)
    updateCurrentModelDisplay(model.id, model.display_name, model.provider, model.id === state.loadedModel);

    updateMenuLoadButton();
    updateHeaderStatus();
}

async function handleSelectModel() {
    if (!selectedModelId) return;

    const model = allModels.find(m => m.id === selectedModelId);
    if (!model) return;

    // If we're in retry mode, load the model immediately instead of just selecting
    if (retryCallback) {
        await selectAndLoadModelForRetry(model);
        return;
    }

    // If we're in selection-only mode, return the model info via callback
    if (selectCallback) {
        const cb = selectCallback;
        selectCallback = null;
        closeModelManager();
        cb({ id: model.id, tier: model.tier, display_name: model.display_name, provider: model.provider });
        return;
    }

    closeModelManager();

    // For OpenRouter models, we use the model ID directly (includes provider/name:free format)
    // For tier-based models (local/anthropic), use the tier name
    const modelToSelect = model.tier || model.id;

    // Update state - mark as selected but NOT loaded yet
    state.selectedModel = modelToSelect;
    state.selectedModelData = model;  // Store full model data for loading later

    syncModelSelectors(model, modelToSelect);

}

/**
 * Load the selected model (called from menu.js when Load button is clicked)
 * @returns {Promise<boolean>} - true if load succeeded
 */
export async function loadSelectedModel() {
    const model = state.selectedModelData;
    if (!model) return false;

    const modelToLoad = model.tier || model.id;

    // Show loading overlay
    const overlay = document.getElementById('connecting-overlay');
    const text = overlay?.querySelector('.connecting-text');
    if (overlay) {
        overlay.classList.remove('hidden');
        if (text) {
            text.textContent = model.provider === 'local'
                ? `Loading ${model.display_name}`
                : `Connecting to ${model.display_name}`;
        }
    }

    try {
        await loadModel(modelToLoad, model._workerId ?? null);

        // Update state - now it's loaded
        state.loadedModel = modelToLoad;
        state.loadedProvider = model.provider || 'local';
        state.isReasoningModel = model.is_reasoning || false;
        state.selectedModel = null;  // Clear selection since it's now loaded
        state.selectedModelData = null;

        updateMenuLoadButton();
        updateHeaderStatus();

        // Update the current model display in the menu (now loaded)
        updateCurrentModelDisplay(model.id, model.display_name, model.provider, true);

        if (overlay) overlay.classList.add('hidden');

        const message = model.provider === 'local'
            ? `${model.display_name} loaded. Ready to chat.`
            : `Connected to ${model.display_name}. Ready to chat.`;
        addSystemMessage(message);

        // Always save session state when model is loaded
        saveSessionStateWithModel(state.loadedModel);

        // If we're in retry mode, trigger the callback
        checkRetryCallback();

        return true;
    } catch (e) {
        console.error('[ModelManager] Load failed:', e);
        if (overlay) overlay.classList.add('hidden');
        addSystemMessage(`Failed to load ${model.display_name}: ${e.message}`);
        return false;
    }
}

// Admin-only compute-worker placement view (null for container users)
let computeWorkers = null;

// Callback to run after model selection in retry mode
let retryCallback = null;
let selectCallback = null;  // Callback for "pick a model" mode (no loading)

/**
 * Open model manager specifically for retry - after selection+load, calls callback
 * @param {Function} onModelLoaded - Callback to run after model is loaded
 */
export function openModelManagerForRetry(onModelLoaded) {
    retryCallback = onModelLoaded;
    openModelManager();
}

/**
 * Open model manager for selection only — no model loading.
 * @param {Function} onSelected - Callback receiving { id, tier, display_name, provider }
 */
export function openModelManagerForSelection(onSelected) {
    selectCallback = onSelected;
    openModelManager();
}

/**
 * Select a model and immediately load it (used for retry flow)
 * @param {Object} model - The model object to select and load
 */
async function selectAndLoadModelForRetry(model) {
    const modelToLoad = model.tier || model.id;

    // Close modal and show loading overlay
    closeModelManager();

    const overlay = document.getElementById('connecting-overlay');
    const text = overlay?.querySelector('.connecting-text');
    if (overlay) {
        overlay.classList.remove('hidden');
        if (text) {
            text.textContent = model.provider === 'local'
                ? `Loading ${model.display_name}`
                : `Connecting to ${model.display_name}`;
        }
    }

    try {
        await loadModel(modelToLoad, model._workerId ?? null);

        // Update state - now it's loaded
        state.loadedModel = modelToLoad;
        state.loadedProvider = model.provider || 'local';
        state.isReasoningModel = model.is_reasoning || false;
        state.selectedModel = null;
        state.selectedModelData = null;

        // Update UI
        updateCurrentModelDisplay(model.id, model.display_name, model.provider, true);
        updateMenuLoadButton();
        updateHeaderStatus();

        if (overlay) overlay.classList.add('hidden');

        // Save session state
        saveSessionStateWithModel(state.loadedModel);

        // Execute the retry callback
        if (retryCallback) {
            const callback = retryCallback;
            retryCallback = null;
            callback();
        }
    } catch (e) {
        console.error('[ModelManager] Load for retry failed:', e);
        if (overlay) overlay.classList.add('hidden');
        addSystemMessage(`Failed to load ${model.display_name}: ${e.message}`);
        retryCallback = null;  // Clear callback on failure
    }
}

/**
 * Called when model is loaded - checks if we're in retry mode
 */
function checkRetryCallback() {
    if (retryCallback) {
        const callback = retryCallback;
        retryCallback = null;
        closeModelManager();
        callback();
    }
}

/**
 * Escape HTML for safe rendering
 */

/**
 * Provider/model logo SVGs (inline for offline use)
 */
const PROVIDER_ICONS = {
    // Ollama/Local - llama icon
    local: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <ellipse cx="12" cy="7" rx="4" ry="3"/>
        <path d="M8 7c-2 0-4 1-4.5 3.5"/>
        <path d="M16 7c2 0 4 1 4.5 3.5"/>
        <path d="M12 10v3"/>
        <ellipse cx="12" cy="16" rx="5" ry="4"/>
        <circle cx="10" cy="15" r="0.5" fill="currentColor"/>
        <circle cx="14" cy="15" r="0.5" fill="currentColor"/>
    </svg>`,

    // Anthropic - stylized "A"
    anthropic: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M13.5 3L19 21h-3.5l-1.2-4H9.7l-1.2 4H5L10.5 3h3zm-1.5 4.2L9.8 14h4.4L12 7.2z"/>
    </svg>`,

    // Groq - lightning bolt (fast inference)
    groq: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    </svg>`,

    // OpenRouter - routing/network symbol
    openrouter: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <circle cx="12" cy="5" r="2.5"/>
        <circle cx="5" cy="19" r="2.5"/>
        <circle cx="19" cy="19" r="2.5"/>
        <path d="M12 7.5v5"/>
        <path d="M12 12.5l-5.5 4"/>
        <path d="M12 12.5l5.5 4"/>
    </svg>`,

    // Google - colorful G (simplified single color)
    google: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866.549 3.921 1.453l2.814-2.814C17.503 2.988 15.139 2 12.545 2 7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
    </svg>`,

    // Meta/Llama - infinity/meta symbol
    meta: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.4c-1.6-2.1-3.3-3.4-5-3.4-2.8 0-5 3.1-5 7s2.2 7 5 7c1.7 0 3.4-1.3 5-3.4 1.6 2.1 3.3 3.4 5 3.4 2.8 0 5-3.1 5-7s-2.2-7-5-7c-1.7 0-3.4 1.3-5 3.4zM7 18c-1.5 0-3-2-3-4s1.5-4 3-4c1 0 2.3 1.2 3.5 3-1.2 1.8-2.5 3-3.5 3zm10 0c-1 0-2.3-1.2-3.5-3 1.2-1.8 2.5-3 3.5-3 1.5 0 3 2 3 4s-1.5 4-3 4z"/>
    </svg>`,

    // Microsoft - four squares
    microsoft: `<svg viewBox="0 0 24 24" fill="currentColor">
        <rect x="3" y="3" width="8" height="8" rx="1"/>
        <rect x="13" y="3" width="8" height="8" rx="1"/>
        <rect x="3" y="13" width="8" height="8" rx="1"/>
        <rect x="13" y="13" width="8" height="8" rx="1"/>
    </svg>`,

    // Qwen/Alibaba - cloud-like
    qwen: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
    </svg>`,

    // DeepSeek - magnifier/depth
    deepseek: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <circle cx="10" cy="10" r="6"/>
        <path d="M21 21l-5.2-5.2"/>
        <path d="M10 7v6"/>
        <path d="M7 10h6"/>
    </svg>`,

    // Mistral - wind/air
    mistral: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 8h13a4 4 0 1 0 0-8H3v8z"/>
        <path d="M3 16h9a4 4 0 1 1 0 8H3v-8z" opacity="0.7"/>
        <path d="M3 12h11a2 2 0 1 0 0-4H3v4z" opacity="0.4"/>
    </svg>`,

    // NVIDIA - stylized eye/chip
    nvidia: `<svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M8.948 8.798v-1.43a6.7 6.7 0 0 1 .424-.018c3.922-.124 6.493 3.374 6.493 3.374s-2.774 3.851-5.75 3.851c-.453 0-.883-.063-1.167-.151v-4.326c1.453.195 1.75.96 2.626 2.096l1.951-1.655s-1.62-1.925-4.148-1.925c-.16 0-.287.006-.43.018v-.001z"/>
        <path d="M8.947 4.156v1.969a7.39 7.39 0 0 1 .85-.052c4.908-.092 8.095 4.27 8.095 4.27s-3.679 4.835-7.647 4.835c-.469 0-.906-.05-1.298-.144v1.615c.345.05.72.081 1.109.081 3.777 0 6.717-2.006 9.456-4.396.453.38 2.303 1.247 2.682 1.636-2.665 2.278-8.857 4.3-12.063 4.3-.411 0-.797-.03-1.184-.074v1.647h12.883V4.156H8.948z"/>
        <path d="M8.947 14.391v-4.326c-1.453.195-1.75.96-2.626 2.096l-1.95-1.655s1.62-1.925 4.147-1.925c.16 0 .287.006.43.018V7.169a6.7 6.7 0 0 0-.424-.018c-3.922-.124-6.493 3.374-6.493 3.374s2.773 3.851 5.75 3.851c.452 0 .882-.063 1.166-.151v.166z" opacity="0.8"/>
    </svg>`,

    // Default/fallback - simple robot head
    default: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="4" y="6" width="16" height="14" rx="2"/>
        <circle cx="9" cy="12" r="1.5" fill="currentColor"/>
        <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
        <path d="M9 16h6"/>
        <path d="M12 2v4"/>
    </svg>`
};

/**
 * Get icon for a model based on provider
 */
function getModelIcon(provider, providerName) {
    // Map provider names to icon keys
    const iconMap = {
        'local': 'local',
        'ollama': 'local',
        'anthropic': 'anthropic',
        'groq': 'groq',
        'openrouter': 'openrouter',
        'google': 'google',
        'meta': 'meta',
        'microsoft': 'microsoft',
        'qwen': 'qwen',
        'deepseek': 'deepseek',
        'mistral': 'mistral',
        'nvidia': 'nvidia',
        'nous research': 'default',
        'moonshot': 'default',
        'open r1': 'default',
        'reka': 'default',
        'allen ai': 'default'
    };

    // Try provider_name first (more specific), then provider
    const key = iconMap[providerName?.toLowerCase()] || iconMap[provider?.toLowerCase()] || 'default';
    return PROVIDER_ICONS[key] || PROVIDER_ICONS.default;
}

/**
 * Set whether tooling filter is required (agent has Tier 1 tools)
 * Call this when loading an agent to enforce tooling-capable models only
 * @param {boolean} required - Whether tooling support is required
 */
export function setToolingRequired(required) {
    toolingRequired = required;
}
